import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Radio, Loader2 } from 'lucide-react';
import { oddpoolService } from '@/services/oddpoolService';
import { toast } from 'sonner';
import T from "@/i18n/T";

export default function LiveFeedsViewer() {
  const [kind, setKind] = useState<'macro' | 'crypto'>('crypto');
  const [channels, setChannels] = useState<string[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [messages, setMessages] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await oddpoolService.feedCatalog(kind);
        const names = c.feeds
          .flatMap((e) => {
            const many = e.channels;
            if (Array.isArray(many)) return many as string[];
            const one = e.channel ?? e.name ?? e.feed ?? e.topic;
            return typeof one === 'string' ? [one] : [];
          })
          .filter(Boolean)
          .slice(0, 40);
        if (!cancelled) setChannels(names);
      } catch (e) {
        if (!cancelled) {
          setChannels([]);
          setError((e as Error).message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  const subscribe = async (ch: string) => {
    setActive(ch); setMessages([]); setLoading(true);
    const unsub = oddpoolService.subscribeChannel(ch, (m) => setMessages((prev) => [m, ...prev].slice(0, 50)));
    try { await oddpoolService.startRelay([ch]); }
    catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
    return unsub;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Radio className="h-4 w-4 text-primary" /> Live feeds
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={kind} onValueChange={(v) => setKind(v as 'macro' | 'crypto')}>
          <TabsList>
            <TabsTrigger value="crypto"><T k="auto.crypto-79ad" /></TabsTrigger>
            <TabsTrigger value="macro"><T k="auto.macro-4374" /></TabsTrigger>
          </TabsList>
          <TabsContent value={kind} className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              {channels.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {error ?? 'No live channels available right now.'}
                </p>
              )}
              {channels.map((c) => (
                <Button
                  key={c}
                  size="sm"
                  variant={active === c ? 'default' : 'outline'}
                  onClick={() => subscribe(c)}
                  className="text-xs"
                >
                  {c}
                </Button>
              ))}
            </div>
            {active && (
              <div className="border rounded-md p-3 max-h-80 overflow-auto bg-muted/30">
                <div className="text-xs font-medium mb-2 flex items-center gap-2">
                  Subscribed: {active}
                  {loading && <Loader2 className="h-3 w-3 animate-spin" />}
                </div>
                {messages.length === 0 ? (
                  <p className="text-xs text-muted-foreground"><T k="auto.waiting-for-messages-relay-session-lasts-f6a4" /></p>
                ) : (
                  <pre className="text-[10px] leading-tight whitespace-pre-wrap">{messages.map((m, i) => JSON.stringify(m)).join('\n')}</pre>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
